import type { ContextBlock, PipelineStage, StageResult } from '../types';
import { draftText } from '../draft-text';
import { compress, type CompressResult } from '../../nlp/compressor';
import { protectedRangesFor } from '../../nlp/protected-ranges';

/**
 * Self-imposed wall-clock cap. StageContext has no deadline object — the runner
 * only checks its 2s budget *between* stages and doesn't abort a running one —
 * so B4 polices itself, leaving headroom for the stages after it.
 */
const STAGE_BUDGET_MS = 1200;

/** A stretch of text this stage may hand to the compressor. */
interface Target {
  /** Which context block it lives in, or null for the top-level conversation summary. */
  blockIndex: number | null;
  start: number;
  end: number;
  text: string;
  tokens: number;
}

interface BlockEdit {
  start: number;
  end: number;
  text: string;
}

export const b4SemanticCompression: PipelineStage = {
  id: 'B4',
  group: 'B',
  label: 'Semantic compression',
  description:
    'Compress docstrings, comments, and the conversation summary using a token-classification model, keeping code/literals/your typed message untouched.',
  impact: 'Medium',
  // Lossy in a way the user can actually perceive in the reply, unlike the rest
  // of group B. Earn default-on with benchmark evidence, not by assertion.
  defaultEnabled: false,
  implemented: true,
  params: {
    rate: { type: 'number', default: 0.6, min: 0.1, max: 0.95, label: 'Target keep rate' },
    minSpanTokens: { type: 'number', default: 50, min: 0, max: 2000, label: 'Minimum span size to consider (tokens)' },
    compressDocstrings: { type: 'boolean', default: true, label: 'Compress docstrings' },
    compressComments: { type: 'boolean', default: true, label: 'Compress comments' },
    compressSummary: { type: 'boolean', default: true, label: 'Compress conversation summary' },
  },

  async run(draft, params, ctx): Promise<StageResult> {
    const stageDeadline = performance.now() + STAGE_BUDGET_MS;
    const tokensBefore = ctx.estimateTokens(draftText(draft));

    const rate = clamp(Number(params.rate) || 0.6, 0.1, 0.95);
    const minSpanTokens = Math.max(0, Number(params.minSpanTokens) || 0);
    const compressDocstrings = params.compressDocstrings !== false;
    const compressComments = params.compressComments !== false;
    const compressSummary = params.compressSummary !== false;

    const targets = collectTargets(draft, {
      minSpanTokens,
      compressDocstrings,
      compressComments,
      compressSummary,
      estimateTokens: ctx.estimateTokens,
    });

    if (targets.length === 0) {
      return report(draft, tokensBefore, tokensBefore, ['No docstring, comment, or summary spans large enough to compress.']);
    }

    // Biggest first: the model is the slow part, so if the budget runs out
    // partway through we want the fattest wins already banked.
    targets.sort((a, b) => b.tokens - a.tokens);

    const editsByBlock = new Map<number, BlockEdit[]>();
    let summaryText: string | undefined;
    let compressed = 0;
    let ranOutOfTime = false;
    const skipReasons = new Map<CompressResult['status'], number>();

    for (const target of targets) {
      if (performance.now() >= stageDeadline) {
        ranOutOfTime = true;
        break;
      }
      // compress() never throws — every failure comes back as a skipped_* status
      // with the original text, so one bad span can't take the others down.
      const result = await compress({
        text: target.text,
        rate,
        protectedRanges: protectedRangesFor(target.text),
        signal: ctx.signal,
      });

      if (result.status !== 'ok' || result.text === target.text) {
        if (result.status !== 'ok') skipReasons.set(result.status, (skipReasons.get(result.status) ?? 0) + 1);
        continue;
      }

      compressed++;
      if (target.blockIndex === null) {
        summaryText = result.text;
      } else {
        const edits = editsByBlock.get(target.blockIndex) ?? [];
        edits.push({ start: target.start, end: target.end, text: result.text });
        editsByBlock.set(target.blockIndex, edits);
      }
    }

    if (compressed === 0) {
      return report(draft, tokensBefore, tokensBefore, [
        `0 of ${targets.length} candidate span(s) compressed — ${describeSkips(skipReasons, ranOutOfTime)}.`,
      ]);
    }

    const newBlocks: ContextBlock[] = draft.contextBlocks.map((block, index) => {
      const edits = editsByBlock.get(index);
      if (!edits) return block;
      return {
        ...block,
        content: applyEdits(block.content, edits),
        // Offsets just moved, and the compressed text is no longer the
        // docstring B1 tagged — drop the spans rather than let anything
        // downstream trust them.
        spans: undefined,
      };
    });

    const newDraft = {
      ...draft,
      contextBlocks: newBlocks,
      ...(summaryText !== undefined ? { historySummary: summaryText } : {}),
    };
    const tokensAfter = ctx.estimateTokens(draftText(newDraft));

    const notes = [`Compressed ${compressed} of ${targets.length} candidate span(s) at a ${Math.round(rate * 100)}% keep rate.`];
    if (skipReasons.size > 0 || ranOutOfTime) notes.push(`Skipped the rest: ${describeSkips(skipReasons, ranOutOfTime)}.`);

    return report(newDraft, tokensBefore, tokensAfter, notes);
  },
};

function collectTargets(
  draft: Parameters<PipelineStage['run']>[0],
  opts: {
    minSpanTokens: number;
    compressDocstrings: boolean;
    compressComments: boolean;
    compressSummary: boolean;
    estimateTokens: (text: string) => number;
  },
): Target[] {
  const targets: Target[] = [];

  draft.contextBlocks.forEach((block, blockIndex) => {
    for (const span of block.spans ?? []) {
      const wanted =
        (span.kind === 'docstring' && opts.compressDocstrings) || (span.kind === 'comment' && opts.compressComments);
      if (!wanted) continue;
      // Fail closed on a span that doesn't fit the content it claims to describe.
      if (span.start < 0 || span.end > block.content.length || span.end <= span.start) continue;

      const text = block.content.slice(span.start, span.end);
      const tokens = opts.estimateTokens(text);
      if (tokens < opts.minSpanTokens) continue;
      targets.push({ blockIndex, start: span.start, end: span.end, text, tokens });
    }
  });

  if (opts.compressSummary && draft.historySummary) {
    // The summary has no ContextBlock of its own — it's a top-level
    // DraftRequest field produced by D1 — so it rides along as a synthetic
    // whole-string target with a null blockIndex.
    const tokens = opts.estimateTokens(draft.historySummary);
    if (tokens >= opts.minSpanTokens) {
      targets.push({ blockIndex: null, start: 0, end: draft.historySummary.length, text: draft.historySummary, tokens });
    }
  }

  return targets;
}

/** Splices right-to-left so an earlier edit never invalidates a later one's offsets. */
function applyEdits(content: string, edits: BlockEdit[]): string {
  let out = content;
  for (const edit of [...edits].sort((a, b) => b.start - a.start)) {
    out = out.slice(0, edit.start) + edit.text + out.slice(edit.end);
  }
  return out;
}

function describeSkips(reasons: Map<CompressResult['status'], number>, ranOutOfTime: boolean): string {
  const parts: string[] = [];
  const label: Record<string, string> = {
    skipped_model_unavailable: 'compressor model unavailable',
    skipped_timeout: 'compressor timed out',
    skipped_too_short: 'span too short to be worth compressing',
    skipped_aborted: 'pipeline aborted',
    ok: 'no change produced',
  };
  for (const [status, count] of reasons) parts.push(`${label[status] ?? status}${count > 1 ? ` (x${count})` : ''}`);
  if (ranOutOfTime) parts.push(`stage time budget of ${STAGE_BUDGET_MS}ms exhausted`);
  return parts.length > 0 ? parts.join('; ') : 'no change produced';
}

function report(
  draft: Parameters<PipelineStage['run']>[0],
  tokensBefore: number,
  tokensAfter: number,
  notes: string[],
): StageResult {
  return {
    draft,
    report: {
      stageId: 'B4',
      tokensBefore,
      tokensAfter,
      tokensSaved: Math.max(0, tokensBefore - tokensAfter),
      notes,
    },
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
